import React from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';

// Plain SVG charts. Deliberately no charting library: this ships into a
// production bundle, the shapes needed here are simple, and an extra dependency
// is a rebuild risk for very little gain.

var PALETTE = ['#1f6feb', '#e07b39', '#2e9e6b', '#a054c9', '#c94f4f',
               '#4aa3c7', '#d4a017', '#7a8b99', '#7b5ea7', '#3f8f5f'];

function colorAt(i) { return PALETTE[i % PALETTE.length]; }

function maxOf(series) {
  var m = 0;
  series.forEach(function(s) {
    (s.values || []).forEach(function(v) { if (v > m) m = v; });
  });
  return m || 1;
}

function truncate(s, n) {
  s = String(s === null || s === undefined ? '' : s);
  return s.length > n ? s.substring(0, n - 1) + '\u2026' : s;
}

var E = React.createElement;

function Legend(props) {
  var series = props.series || [];
  if (series.length <= 1 && !series[0]) return null;
  if (series.length === 1 && !series[0].name) return null;
  return E(Box, {sx:{display:'flex', flexWrap:'wrap', gap:1.5, mt:1}},
    series.map(function(s, i) {
      return E(Box, {key:i, sx:{display:'flex', alignItems:'center', gap:0.5}},
        E(Box, {sx:{width:10, height:10, borderRadius:'2px', bgcolor:colorAt(i)}}),
        E(Typography, {variant:'caption', color:'text.secondary'}, s.name || 'All'));
    }));
}

// Horizontal bars — the readable default when answers are long text.
export function BarChart(props) {
  var labels = props.labels || [];
  var series = props.series || [];
  if (labels.length === 0) return E(Empty);

  var max = maxOf(series);
  var rowH = series.length > 1 ? 14 * series.length + 12 : 24;
  var gap = 8;
  var labelW = 150;
  var width = 560;
  var height = labels.length * (rowH + gap) + 8;

  return E(Box, null,
    E('svg', {viewBox:'0 0 ' + width + ' ' + height, width:'100%',
      style:{maxHeight: Math.max(height, 80), overflow:'visible'}, role:'img'},
      labels.map(function(label, r) {
        var y = r * (rowH + gap);
        return E('g', {key:r},
          E('title', null, label),
          E('text', {x:labelW - 8, y:y + rowH / 2 + 4, textAnchor:'end',
            fontSize:11, fill:'#555'}, truncate(label, 24)),
          series.map(function(s, si) {
            var v = (s.values || [])[r] || 0;
            var barH = series.length > 1 ? 12 : 18;
            var by = y + (rowH - barH * series.length) / 2 + si * barH;
            var w = Math.round((v / max) * (width - labelW - 46));
            return E('g', {key:si},
              E('rect', {x:labelW, y:by, width:Math.max(w, v > 0 ? 2 : 0), height:barH - 2,
                fill:colorAt(si), rx:2}),
              v > 0 ? E('text', {x:labelW + Math.max(w, 2) + 5, y:by + barH - 6,
                fontSize:10, fill:'#666'}, v) : null);
          }));
      })),
    E(Legend, {series:series}));
}

// Vertical bars — better for ordered or short-labelled answers.
export function ColumnChart(props) {
  var labels = props.labels || [];
  var series = props.series || [];
  if (labels.length === 0) return E(Empty);

  var max = maxOf(series);
  var width = 560, height = 220, padB = 46, padL = 32;
  var slot = (width - padL) / labels.length;
  var barW = Math.max(6, (slot - 8) / series.length);

  return E(Box, null,
    E('svg', {viewBox:'0 0 ' + width + ' ' + height, width:'100%', role:'img'},
      E('line', {x1:padL, y1:height - padB, x2:width, y2:height - padB, stroke:'#ddd'}),
      labels.map(function(label, c) {
        var x0 = padL + c * slot + 4;
        return E('g', {key:c},
          E('title', null, label),
          series.map(function(s, si) {
            var v = (s.values || [])[c] || 0;
            var h = Math.round((v / max) * (height - padB - 16));
            return E('rect', {key:si, x:x0 + si * barW, y:height - padB - h,
              width:barW - 2, height:Math.max(h, v > 0 ? 2 : 0), fill:colorAt(si), rx:2});
          }),
          E('text', {x:x0 + (barW * series.length) / 2, y:height - padB + 12,
            fontSize:9, fill:'#666', textAnchor:'middle',
            transform: labels.length > 6
              ? 'rotate(35 ' + (x0 + (barW * series.length) / 2) + ' ' + (height - padB + 12) + ')'
              : null},
            truncate(label, labels.length > 6 ? 14 : 12)));
      }),
      E('text', {x:4, y:14, fontSize:10, fill:'#999'}, max)),
    E(Legend, {series:series}));
}

export function PieChart(props) {
  var labels = props.labels || [];
  var values = ((props.series || [])[0] || {}).values || [];
  var total = values.reduce(function(a, b) { return a + b; }, 0);
  if (!total) return E(Empty);

  var cx = 110, cy = 110, r = 92, inner = 46;
  var angle = -Math.PI / 2;
  var paths = [];

  values.forEach(function(v, i) {
    if (!v) return;
    var slice = (v / total) * Math.PI * 2;
    var end = angle + slice;
    var large = slice > Math.PI ? 1 : 0;
    var x1 = cx + r * Math.cos(angle), y1 = cy + r * Math.sin(angle);
    var x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end);
    var ix2 = cx + inner * Math.cos(end), iy2 = cy + inner * Math.sin(end);
    var ix1 = cx + inner * Math.cos(angle), iy1 = cy + inner * Math.sin(angle);
    paths.push(E('path', {key:i, fill:colorAt(i),
      d:'M' + x1 + ' ' + y1 + ' A' + r + ' ' + r + ' 0 ' + large + ' 1 ' + x2 + ' ' + y2 +
        ' L' + ix2 + ' ' + iy2 + ' A' + inner + ' ' + inner + ' 0 ' + large + ' 0 ' + ix1 + ' ' + iy1 + ' Z'},
      E('title', null, labels[i] + ': ' + v)));
    angle = end;
  });

  return E(Box, {sx:{display:'flex', gap:2, alignItems:'center', flexWrap:'wrap'}},
    E('svg', {viewBox:'0 0 220 220', width:200, height:200, role:'img'},
      paths,
      E('text', {x:cx, y:cy + 5, fontSize:20, textAnchor:'middle', fill:'#333'}, total)),
    E(Box, null,
      labels.map(function(l, i) {
        if (!values[i]) return null;
        var pct = Math.round((values[i] / total) * 100);
        return E(Box, {key:i, sx:{display:'flex', alignItems:'center', gap:0.75, mb:0.25}},
          E(Box, {sx:{width:10, height:10, borderRadius:'2px', bgcolor:colorAt(i), flexShrink:0}}),
          E(Typography, {variant:'caption'}, truncate(l, 28) + ' \u2014 ' + values[i] + ' (' + pct + '%)'));
      })));
}

export function LineChart(props) {
  var labels = props.labels || [];
  var series = props.series || [];
  if (labels.length === 0) return E(Empty);

  var max = maxOf(series);
  var width = 560, height = 220, padB = 40, padL = 34;
  var stepX = labels.length > 1 ? (width - padL - 10) / (labels.length - 1) : 0;

  function pointsFor(s) {
    return (s.values || []).map(function(v, i) {
      var x = padL + i * stepX;
      var y = height - padB - (v / max) * (height - padB - 16);
      return x + ',' + y;
    }).join(' ');
  }

  return E(Box, null,
    E('svg', {viewBox:'0 0 ' + width + ' ' + height, width:'100%', role:'img'},
      E('line', {x1:padL, y1:height - padB, x2:width, y2:height - padB, stroke:'#ddd'}),
      E('text', {x:4, y:14, fontSize:10, fill:'#999'}, max),
      series.map(function(s, si) {
        return E('g', {key:si},
          E('polyline', {points:pointsFor(s), fill:'none', stroke:colorAt(si), strokeWidth:2}),
          (s.values || []).map(function(v, i) {
            return E('circle', {key:i, cx:padL + i * stepX,
              cy:height - padB - (v / max) * (height - padB - 16), r:3, fill:colorAt(si)},
              E('title', null, labels[i] + ': ' + v));
          }));
      }),
      labels.map(function(l, i) {
        if (labels.length > 12 && i % 2 === 1) return null;
        return E('text', {key:i, x:padL + i * stepX, y:height - padB + 14,
          fontSize:9, fill:'#666', textAnchor:'middle'}, truncate(l, 10));
      })),
    E(Legend, {series:series}));
}

export function DataTable(props) {
  var labels = props.labels || [];
  var series = props.series || [];
  if (labels.length === 0) return E(Empty);
  var multi = series.length > 1 || (series[0] && series[0].name);

  return E(Table, {size:'small'},
    E(TableHead, null,
      E(TableRow, null,
        E(TableCell, null, 'Answer'),
        multi
          ? series.map(function(s, i) { return E(TableCell, {key:i, align:'right'}, s.name || 'All'); })
          : E(TableCell, {align:'right'}, 'Count'))),
    E(TableBody, null,
      labels.map(function(l, r) {
        return E(TableRow, {key:r},
          E(TableCell, {sx:{wordBreak:'break-word'}}, l),
          series.map(function(s, i) {
            return E(TableCell, {key:i, align:'right'}, (s.values || [])[r] || 0);
          }));
      })));
}

export function Stat(props) {
  var series = props.series || [];
  var values = (series[0] || {}).values || [];
  var total = values.reduce(function(a, b) { return a + b; }, 0);
  return E(Box, {sx:{py:2, textAlign:'center'}},
    E(Typography, {variant:'h3', fontWeight:700, color:'primary.main'}, total),
    E(Typography, {variant:'body2', color:'text.secondary'},
      (props.labels || []).length + ' distinct answers'));
}

function Empty() {
  return E(Box, {sx:{py:4, textAlign:'center'}},
    E(Typography, {variant:'body2', color:'text.secondary'},
      'No answers to this question yet'));
}

export function renderChart(chart) {
  var props = { labels: chart.labels || [], series: chart.series || [] };
  switch (chart.type) {
    case 'column': return E(ColumnChart, props);
    case 'pie':    return E(PieChart, props);
    case 'line':   return E(LineChart, props);
    case 'table':  return E(DataTable, props);
    case 'stat':   return E(Stat, props);
    default:       return E(BarChart, props);
  }
}
